import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button, ConfirmDialog, ErrorNotice, Panel, Pill, SkeletonTable } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  SALESFORCE_SECTION_ID,
  CONNECT_RESULT,
  CONNECT_RESULT_PARAM,
  REP_CONNECTION_QUERY_KEY,
  REP_CONNECTION_STATUS,
  createRepConnectUrl,
  disconnectRepConnection,
  isNotAllowed,
  readRepConnection,
} from '@/lib/sales-workspace';


/** What a failed connect means, in the rep's words (reason codes from the callback). */
const CONNECT_FAILURE: Readonly<Record<string, string>> = {
  denied: 'Salesforce sign-in was canceled. Nothing was connected.',
  wrong_salesforce_user: 'You signed in to Salesforce as a different person. Sign in with your own Salesforce login.',
  no_linked_salesforce_user: 'Your account is not linked to a Salesforce user yet. Ask an admin to link it, then try again.',
  wrong_org: 'That login belongs to a different Salesforce org. Sign in to the Best Corporate Events org.',
};
const CONNECT_FAILURE_DEFAULT = 'Salesforce could not be connected. Try again in a minute.';

/**
 * "Connect Salesforce" on your own profile: links your Salesforce login so
 * the changes you make in bcecloud are saved in Salesforce under your name.
 *
 * Dark: the API answers 403 to everyone not on its allow-list (superadmins
 * included), and then this renders nothing at all.
 *
 * @returns The section, or null for anyone the API does not admit.
 */
export function SalesforceConnectionSection(): React.ReactElement | null {
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [confirming, setConfirming] = useState(false);

  const connection = useQuery({
    queryKey: REP_CONNECTION_QUERY_KEY,
    queryFn: ({ signal }) => readRepConnection(signal),
    retry: (count, error) => !isNotAllowed(error) && count < 2,
  });

  const connect = useMutation({
    mutationFn: createRepConnectUrl,
    onSuccess: (url) => {
      window.location.href = url;
    },
  });

  const disconnect = useMutation({
    mutationFn: disconnectRepConnection,
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: REP_CONNECTION_QUERY_KEY });
    },
  });

  if (connection.isError && isNotAllowed(connection.error)) return null;

  const result = params.get(CONNECT_RESULT_PARAM);
  const failure = result === CONNECT_RESULT.error ? (CONNECT_FAILURE[params.get('reason') ?? ''] ?? CONNECT_FAILURE_DEFAULT) : null;
  const data = connection.data;

  return (
    <div id={SALESFORCE_SECTION_ID} className="mt-6 scroll-mt-6">
      <h2 className="mb-1 text-portal-section font-bold tracking-[-0.4px]">Salesforce</h2>
      <p className="mb-3 text-portal-small text-portal-muted">
        Connect your Salesforce login so the changes you make here are saved in Salesforce under your name.
      </p>

      {failure ? <ErrorNotice inline title="Not connected." message={failure} /> : null}
      {result === CONNECT_RESULT.connected && data?.status === REP_CONNECTION_STATUS.connected ? (
        <p role="status" className="mb-4 rounded-portal-card bg-portal-success-bg p-3 text-portal-small text-portal-success">
          Salesforce is connected. Your changes will be saved as you.
        </p>
      ) : null}

      <Panel className="p-5">
        {connection.isPending ? (
          <SkeletonTable rows={1} columns={['w-[50%]', 'w-[20%]']} label="Checking your Salesforce connection…" />
        ) : connection.isError ? (
          <ErrorNotice inline title="Status unavailable." message={errorMessage(connection.error)} />
        ) : !data?.configured ? (
          <p className="text-portal-small text-portal-muted">
            Connecting Salesforce is not set up on this server yet.
          </p>
        ) : data.status === REP_CONNECTION_STATUS.needsReconnect ? (
          <ErrorNotice
            inline
            tone="warning"
            title="Reconnect needed."
            message="Salesforce no longer accepts your saved login (it was revoked or expired). Reconnect to keep saving changes as you."
            action={
              <Button onClick={() => connect.mutate()} disabled={connect.isPending}>
                {connect.isPending ? 'Opening Salesforce…' : 'Reconnect Salesforce'}
              </Button>
            }
          />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="min-w-0">
              <Pill tone={data.status === REP_CONNECTION_STATUS.connected ? 'success' : 'neutral'}>
                {data.status === REP_CONNECTION_STATUS.connected ? 'Connected' : 'Not connected'}
              </Pill>
              <span className="mt-1 block text-portal-small text-portal-muted">
                {data.status === REP_CONNECTION_STATUS.connected
                  ? `Signed in as ${data.salesforceUsername ?? 'your Salesforce user'}${data.connectedAt ? ` since ${new Date(data.connectedAt).toLocaleDateString()}` : ''}.`
                  : 'You will sign in to Salesforce, then come straight back here.'}
              </span>
            </span>
            {data.status === REP_CONNECTION_STATUS.connected ? (
              <Button variant="secondary" onClick={() => setConfirming(true)}>
                Disconnect
              </Button>
            ) : (
              <Button onClick={() => connect.mutate()} disabled={connect.isPending}>
                {connect.isPending ? 'Opening Salesforce…' : 'Connect Salesforce'}
              </Button>
            )}
          </div>
        )}
        {connect.isError ? (
          <div className="mt-3">
            <ErrorNotice inline title="Could not open Salesforce." message={errorMessage(connect.error)} />
          </div>
        ) : null}
      </Panel>

      <ConfirmDialog
        open={confirming}
        tone="danger"
        title="Disconnect Salesforce?"
        message="Your saved Salesforce login is revoked. Changes you make here stop being saved to Salesforce until you connect again."
        confirmLabel="Disconnect"
        onConfirm={() => disconnect.mutate()}
        onCancel={() => {
          if (!disconnect.isPending) setConfirming(false);
        }}
        pending={disconnect.isPending}
        error={disconnect.isError ? errorMessage(disconnect.error) : undefined}
      />
    </div>
  );
}
